import Swiper from 'swiper';
import { Navigation, Keyboard, Pagination, Autoplay } from 'swiper/modules';

class FullwidthImageSlider extends HTMLElement {
  connectedCallback() {
    this.initSwiper();
  }

  disconnectedCallback() {
    this.swiper?.destroy();
  }

  initSwiper() {
    const swiperContainer = this.querySelector('.swiper');
    if (!swiperContainer) return;

    const paginationEl = this.querySelector('.swiper-pagination');
    const mobilePaginationEl = this.#getMobilePaginationEl();
    const hasNavigation = this.querySelector('.next-button') !== null;
    const hasPagination = paginationEl !== null;
    const autoplay = this.dataset.autoplay === 'true';
    const autoplaySpeed = parseInt(this.dataset.autoplaySpeed, 10) * 1000 || 5000;

    const modules = [Keyboard];
    if (hasNavigation) modules.push(Navigation);
    if (hasPagination || mobilePaginationEl) modules.push(Pagination);
    if (autoplay) modules.push(Autoplay);

    // Build pagination elements array for both desktop and mobile
    const paginationElements = [];
    if (paginationEl) paginationElements.push(paginationEl);
    if (mobilePaginationEl) paginationElements.push(mobilePaginationEl);

    this.swiper = new Swiper(swiperContainer, {
      modules,
      slidesPerView: 1,
      spaceBetween: 0,
      loop: true,
      keyboard: {
        enabled: true,
        onlyInViewport: true,
      },
      autoplay: autoplay
        ? {
            delay: autoplaySpeed,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          }
        : false,
      pagination:
        paginationElements.length > 0
          ? {
              el: paginationElements,
              clickable: true,
              bulletClass:
                'w-2.5 h-2.5 rounded-full bg-gray-300 md:bg-white/50 cursor-pointer transition-all',
              bulletActiveClass: '!bg-primary !w-6 md:!bg-white',
            }
          : false,
      navigation: hasNavigation
        ? {
            nextEl: this.querySelector('.next-button'),
            prevEl: this.querySelector('.prev-button'),
          }
        : false,
      on: {
        slideChange: (swiper) => {
          this.#handleVideoPlayback(swiper);
          this.#handleBelowSliderContentChange(swiper);
        },
      },
    });
  }

  #getMobilePaginationEl() {
    // Mobile pagination is a sibling element outside the custom element
    const parent = this.closest('.section-fullwidth-image-slider') || this.parentElement;
    return parent?.querySelector('.swiper-pagination-mobile');
  }

  #getBelowSliderContentWrapper() {
    const parent = this.closest('.section-fullwidth-image-slider') || this.parentElement;
    return parent?.querySelector('.below-slider-content-wrapper');
  }

  #handleVideoPlayback(swiper) {
    const allVideos = this.querySelectorAll('video');
    for (const video of allVideos) video.pause();

    const activeSlide = swiper.slides[swiper.activeIndex];
    const activeVideo = activeSlide?.querySelector('video');

    if (activeVideo) activeVideo.play();
  }

  #handleBelowSliderContentChange(swiper) {
    const belowSliderWrapper = this.#getBelowSliderContentWrapper();
    if (!belowSliderWrapper) return;

    const belowSliderContents = belowSliderWrapper.querySelectorAll('.slide-content-below');
    if (belowSliderContents.length === 0) return;

    // Get real index (accounting for loop mode)
    const realIndex = swiper.realIndex;

    for (const content of belowSliderContents) {
      const slideIndex = parseInt(content.dataset.slideIndex, 10);
      if (slideIndex === realIndex) {
        content.classList.remove('hidden');
      } else {
        content.classList.add('hidden');
      }
    }
  }
}

customElements.define('fullwidth-image-slider', FullwidthImageSlider);
